import React from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import styled from "styled-components";

import mailLogo from "../../images/Email_Icon_Blk.png";
import bowler_hand from "../../images/dashboard/Hand_Border_Icon.svg";
import { getPhotoForUserId } from "../../utils/utils";
import theme from "../../theme/feedback";

const Panel = styled.div`
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.lg};
  box-shadow: 0px 2px 8px rgba(21, 34, 50, 0.06);
  padding: ${theme.spacing.lg};
  position: absolute;
  inset: 0;
  overflow-y: auto;

  @media only screen and (max-width: 991px) {
    position: static;
    inset: auto;
    width: 84vw;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeLg};
  font-weight: ${theme.font.weightSemibold};
  color: ${theme.color.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  padding: 0;

  &:not(:first-child) {
    margin-top: ${theme.spacing.lg};
    padding-top: ${theme.spacing.md};
    border-top: 1px solid ${theme.color.border};
  }
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  margin: 0 -${theme.spacing.sm};
  border-radius: ${theme.radius.md};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${theme.color.surfaceAlt};
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Name = styled.span`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeMd};
  font-weight: ${theme.font.weightSemibold};
  color: ${theme.color.text};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const SubName = styled.span`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeSm};
  color: ${theme.color.text};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const Meta = styled.span`
  font-family: ${theme.font.family};
  font-size: 11px;
  color: ${theme.color.textMuted};
`;

const TrailingIcon = styled.img`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  opacity: 0.7;
`;

const EmptyState = styled.div`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeSm};
  color: ${theme.color.textMuted};
  padding: ${theme.spacing.sm} 0;
`;

export const MostRecents = ({
  customerTitle = "Most Recent Customers",
  speecsTitle = "Most Recent Spec Sheets",
  userId,
  dashboard_data,
}) => {
  const navigate = useNavigate();
  const getPhoto = getPhotoForUserId(userId);

  const handleUserNavigate = (id) => navigate("/bowler/" + id.toString());

  const getDatesSpecsheet = (specsheetDate, title) =>
    `${title}: ${moment(specsheetDate).format("L")} @ ${moment(
      specsheetDate
    ).format("LT")}`;

  const getDatesCustomer = (customer) =>
    `CR: ${moment(customer.created_at).format("L")} @ ${moment(
      customer.created_at
    ).format("LT")}`;

  const generatePlaceholderImage = (name, lastName) => {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#00187E";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 28px Poppins";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const initials = `${name?.charAt(0).toUpperCase()}${lastName
      ?.charAt(0)
      .toUpperCase()}`;
    ctx.fillText(initials, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL("image/png");
  };

  const photoFor = (entry) =>
    getPhoto(entry.photo) === null
      ? generatePlaceholderImage(entry.firstname, entry.lastname)
      : getPhoto(entry.photo);

  const customers = dashboard_data.most_recent.customers;
  const specsheets = dashboard_data.most_recent.specsheets;

  return (
    <Panel>
      <SectionTitle>{customerTitle}</SectionTitle>
      {customers.length === 0 && <EmptyState>—</EmptyState>}
      {customers.map((customer, index) => (
        <Item key={index} onClick={() => handleUserNavigate(customer.id)}>
          <Avatar src={photoFor(customer)} alt="" />
          <ItemBody>
            <Name>{customer.firstname + " " + customer.lastname}</Name>
            <Meta>{getDatesCustomer(customer)}</Meta>
          </ItemBody>
          <TrailingIcon src={mailLogo} alt="" />
        </Item>
      ))}

      <SectionTitle>{speecsTitle}</SectionTitle>
      {specsheets.length === 0 && <EmptyState>—</EmptyState>}
      {specsheets.map((specSheet, index) => (
        <Item
          key={index}
          onClick={() => handleUserNavigate(specSheet.customer_id)}
        >
          <Avatar src={photoFor(specSheet)} alt="" />
          <ItemBody>
            <Name>{specSheet.firstname + " " + specSheet.lastname}</Name>
            <SubName>{specSheet.name}</SubName>
            <Meta>{getDatesSpecsheet(specSheet.created_at, "CR")}</Meta>
            <Meta>{getDatesSpecsheet(specSheet.updated_at, "UP")}</Meta>
          </ItemBody>
          <TrailingIcon src={bowler_hand} alt="" />
        </Item>
      ))}
    </Panel>
  );
};
